// src/components/AuthModal/AuthModal.jsx
// Modal de login/registro que aparece SOBRE el marketplace (soft wall).
// Hereda el "look" de las landings: fondo gaming, BTC en vivo y social-proof.
// Reusa AuthContext.login + los endpoints /api/login y /api/register.
import React, { useState, useEffect } from 'react';
import { FiX, FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiArrowRight, FiUsers, FiTrendingUp, FiZap, FiGlobe, FiAward } from 'react-icons/fi';
import { FaWallet } from 'react-icons/fa';
import { useAuth } from '../../Context/AuthContext';
import AuthBackground from './AuthBackground';
import WalletLogin from './WalletLogin';
import WalletOnboarding from './WalletOnboarding';
import './AuthModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
// El login por wallet (SIWE) solo se ofrece si hay App ID de Privy configurado.
const WALLET_LOGIN_ENABLED = !!process.env.REACT_APP_PRIVY_APP_ID;

const LIVE_STATS = [
  { text: '247 traders activos ahora', Icon: FiUsers },
  { text: '$2.3M operados hoy',         Icon: FiTrendingUp },
  { text: 'Cierre promedio: 4.2 min',  Icon: FiZap },
  { text: '16 países conectados',       Icon: FiGlobe },
  { text: 'Top hoy: @SiliconHash',      Icon: FiAward },
];

export default function AuthModal({ open, onClose, onSuccess, initialTab = 'login' }) {
  const { login } = useAuth();
  const [tab, setTab]           = useState(initialTab);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const [statIdx, setStatIdx]   = useState(0);
  const [btcPrice, setBtcPrice] = useState(null);
  const [btcDir, setBtcDir]     = useState(0); // 1 = sube, -1 = baja, 0 = sin cambio
  const [walletFlow, setWalletFlow] = useState(false); // onboarding guiado de creación de wallet

  useEffect(() => {
    if (open) { setTab(initialTab); setError(''); setWalletFlow(false); }
  }, [open, initialTab]);

  // Rotación del ticker de social-proof (solo con el modal abierto)
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setStatIdx(i => (i + 1) % LIVE_STATS.length), 3000);
    return () => clearInterval(t);
  }, [open]);

  // Precio de BTC en vivo (solo con el modal abierto)
  useEffect(() => {
    if (!open) return;
    let prev = null;
    const fetchBtc = async () => {
      try {
        const res  = await fetch('https://data-api.binance.vision/api/v3/ticker/price?symbol=BTCUSDT');
        const data = await res.json();
        const raw  = parseFloat(data.price);
        if (prev !== null && raw !== prev) setBtcDir(raw > prev ? 1 : -1);
        prev = raw;
        setBtcPrice(raw.toLocaleString('en-US', { maximumFractionDigits: 0 }));
      } catch { setBtcPrice(null); }
    };
    fetchBtc();
    const t = setInterval(fetchBtc, 15000);
    return () => clearInterval(t);
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Completa email y contraseña.'); return; }
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (tab === 'register' && !username.trim()) { setError('Elige un nombre de usuario.'); return; }
    setLoading(true);
    try {
      const path = tab === 'login' ? '/api/login' : '/api/register';
      const body = tab === 'login'
        ? { email: email.trim().toLowerCase(), password }
        : { email: email.trim().toLowerCase(), password, username: username.trim(), name: username.trim() };
      const res  = await fetch(`${API_URL}${path}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok)     throw new Error(data.message || 'No se pudo completar. Intenta de nuevo.');
      if (!data.token) throw new Error('Credenciales incorrectas.');
      login(data.user ?? { email }, data.token);
      onSuccess?.();
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'No se pudo conectar al servidor.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  const StatIcon = LIVE_STATS[statIdx].Icon;

  return (
    <div className="am-overlay" onMouseDown={onClose}>
      <AuthBackground />
      <div className="am-modal" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="am-close" onClick={onClose} aria-label="Cerrar"><FiX /></button>

        <div className="am-head">
          <img src="/assets/Logo-White.png" alt="Bitplify" className="am-logo am-logo-dark" />
          <img src="/assets/Logo.png"       alt="Bitplify" className="am-logo am-logo-light" />
          <h2 className="am-title">{tab === 'login' ? 'Entra para operar' : 'Crea tu cuenta'}</h2>
          <p className="am-sub">El mercado sos vos. Sumate en segundos.</p>
        </div>

        {walletFlow ? (
          <WalletOnboarding onSuccess={onSuccess} onBack={() => setWalletFlow(false)} />
        ) : (
        <>
        {/* Social-proof en vivo (hereda el efecto de las landings) */}
        <div className="am-live">
          <span className="am-live-ticker">
            <span className="am-live-dot" />
            <span className="am-live-text" key={statIdx}><StatIcon className="am-live-ic" />{LIVE_STATS[statIdx].text}</span>
          </span>
          {btcPrice && (
            <span className="am-btc">
              <span className="fin-label am-btc-label">BTC</span>
              <span className="fin-num am-btc-price">${btcPrice}</span>
              <span className={`fin-delta am-btc-delta ${btcDir < 0 ? 'fin-down' : 'fin-up'}`}>{btcDir < 0 ? '▼' : '▲'}</span>
            </span>
          )}
        </div>

        <div className="am-tabs">
          <button className={`am-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(''); }}>Entrar</button>
          <button className={`am-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); setError(''); }}>Crear cuenta</button>
        </div>

        <form className="am-form" onSubmit={submit}>
          {tab === 'register' && (
            <label className="am-field">
              <FiUser className="am-ic" />
              <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Nombre de usuario" autoComplete="username" />
            </label>
          )}
          <label className="am-field">
            <FiMail className="am-ic" />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" autoComplete="email" />
          </label>
          <label className="am-field">
            <FiLock className="am-ic" />
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Contraseña"
              autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
            />
            <button type="button" className="am-eye" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
              {showPw ? <FiEyeOff /> : <FiEye />}
            </button>
          </label>

          {error && <div className="am-error">{error}</div>}

          <button type="submit" className="am-submit" disabled={loading}>
            {loading ? <span className="am-spin" /> : <>{tab === 'login' ? 'Entrar' : 'Crear cuenta'} <FiArrowRight /></>}
          </button>
        </form>

        {/* Alternativa: autocustodia con wallet (secundario) */}
        {WALLET_LOGIN_ENABLED && (
          <>
            <div className="am-divider"><span>o con tu wallet</span></div>
            {tab === 'register' ? (
              <>
                <button type="button" className="am-wallet-btn am-wallet-create" onClick={() => setWalletFlow(true)}>
                  <FaWallet className="am-wallet-ic" /> Crear mi wallet <FiArrowRight />
                </button>
                <p className="am-wallet-hint">
                  🔒 <strong>Autocustodia:</strong> tu plata vive en tu wallet, no en Bitplify. Sin semillas ni contraseñas que memorizar.
                </p>
              </>
            ) : (
              <WalletLogin onSuccess={onSuccess} />
            )}
          </>
        )}

        {tab === 'login' ? (
          <a className="am-forgot" href="/forgot">¿Olvidaste tu contraseña?</a>
        ) : (
          <a className="am-fullreg" href="/register">
            <FiAward className="am-fullreg-ic" /> Registro completo — desbloquea recompensas <FiArrowRight />
          </a>
        )}

        <p className="am-foot">
          {tab === 'login' ? '¿Aún no tienes cuenta? ' : '¿Ya tienes cuenta? '}
          <button className="am-link" onClick={() => { setTab(tab === 'login' ? 'register' : 'login'); setError(''); }}>
            {tab === 'login' ? 'Regístrate' : 'Entra'}
          </button>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
